import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import {
  Film,
  Headphones,
  Image as ImageIcon,
  FileText,
  Calendar,
  Clock,
  Folder,
  Search,
  Sparkles,
  ArrowRight,
  Award,
  Volume2,
  Eye,
} from 'lucide-react';
import { jubileeStorageService } from './services/jubileeStorageService';
import JubileeVideoPlayerModal from './viewers/JubileeVideoPlayerModal';
import JubileeDocumentViewerModal from './viewers/JubileeDocumentViewerModal';
import JubileeImageLightbox from './viewers/JubileeImageLightbox';
import jubileeLogo from '../../assets/img/Jubilee-Logo.jpeg';

export default function JubileeDashboard({ onNavigate, onPlayAudioTrack }) {
  const [searchInputValue, setSearchInputValue] = useState('');

  // Modals for featured preview
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [featuredDoc, setFeaturedDoc] = useState(null);
  const [featuredImageIdx, setFeaturedImageIdx] = useState(0);
  const [showImageLightbox, setShowImageLightbox] = useState(false);

  const stats = useMemo(() => jubileeStorageService.getStats(), []);
  const featuredItems = useMemo(() => jubileeStorageService.getFeaturedMedia(), []);
  const allVideos = useMemo(() => jubileeStorageService.getMediaByType('video'), []);
  const allImages = useMemo(() => jubileeStorageService.getMediaByType('image'), []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate('search', { query: searchInputValue });
    }
  };

  const handleFeaturedItemClick = (item) => {
    if (item.mediaType === 'video') {
      setFeaturedVideo(item);
    } else if (item.mediaType === 'audio') {
      if (onPlayAudioTrack) onPlayAudioTrack(item);
    } else if (item.mediaType === 'document') {
      setFeaturedDoc(item);
    } else if (item.mediaType === 'image') {
      const idx = allImages.findIndex((img) => img.id === item.id);
      setFeaturedImageIdx(idx >= 0 ? idx : 0);
      setShowImageLightbox(true);
    }
  };

  return (
    <div className="jubilee-dashboard">
      {/* 1. HERO BANNER */}
      <section className="jubilee-hero text-center position-relative">
        <Container style={{ maxWidth: '960px' }}>
          {/* Official Jubilee Logo Display */}
          <div className="d-flex justify-content-center mb-3">
            <div
              className="rounded-circle p-1 bg-white shadow-2xl position-relative"
              style={{
                width: '110px',
                height: '110px',
                border: '3px solid #d4af37',
                boxShadow: '0 8px 30px rgba(212, 175, 55, 0.4)',
              }}
            >
              <img
                src={jubileeLogo}
                alt="MDU Platinum Jubilee Logo"
                className="w-100 h-100 rounded-circle object-fit-cover"
                onError={(e) => {
                  e.target.src = process.env.PUBLIC_URL + '/assets/img/Jubilee-Logo.jpeg';
                }}
              />
            </div>
          </div>

          <div className="jubilee-hero-badge">
            <Sparkles size={16} className="text-warning" />
            <span>1951 - 2026 • 75 Years of Grace & Scholarship</span>
          </div>

          <h1 className="jubilee-hero-title mb-2">
            MDU Platinum Jubilee
          </h1>
          <div className="text-warning fw-semibold fs-5 text-uppercase" style={{ letterSpacing: '2px' }}>
            Digital Archive & Heritage Collection
          </div>

          <p className="jubilee-hero-subtitle">
            Preserving the living memory, historical documents, liturgical audio recordings, photographic treasures,
            and commemorative films of the Jesuit Madurai Province (JEMPARC).
          </p>

          {/* Quick Search Bar inside Hero */}
          <form onSubmit={handleSearchSubmit} className="mx-auto mt-4" style={{ maxWidth: '640px' }}>
            <div className="input-group input-group-lg shadow-2xl rounded-pill overflow-hidden bg-white p-1">
              <span className="input-group-text bg-white border-0 ps-3">
                <Search size={22} className="text-success" />
              </span>
              <input
                type="text"
                className="form-control border-0 shadow-none fs-6"
                placeholder="Search historical records, decrees, speakers, photos..."
                value={searchInputValue}
                onChange={(e) => setSearchInputValue(e.target.value)}
              />
              <button
                type="submit"
                className="btn btn-success rounded-pill px-4 fw-semibold d-flex align-items-center gap-1"
              >
                Search <ArrowRight size={16} />
              </button>
            </div>
          </form>

          {/* Quick Browse Modes Links */}
          <div className="d-flex flex-wrap justify-content-center gap-3 mt-4 pt-2">
            <span className="text-light text-opacity-75 small align-self-center me-1">Quick Browse:</span>
            <button
              className="btn btn-sm btn-outline-light rounded-pill px-3 d-flex align-items-center gap-1 border-opacity-50"
              onClick={() => onNavigate('events')}
            >
              <Calendar size={14} className="text-warning" /> Browse Events
            </button>
            <button
              className="btn btn-sm btn-outline-light rounded-pill px-3 d-flex align-items-center gap-1 border-opacity-50"
              onClick={() => onNavigate('timeline')}
            >
              <Clock size={14} className="text-info" /> 75-Year Timeline
            </button>
            <button
              className="btn btn-sm btn-outline-light rounded-pill px-3 d-flex align-items-center gap-1 border-opacity-50"
              onClick={() => onNavigate('collections')}
            >
              <Folder size={14} className="text-success" /> Thematic Collections
            </button>
          </div>
        </Container>
      </section>

      {/* 2. STATISTICAL MILESTONE COUNTERS */}
      <section className="py-3 bg-white border-bottom shadow-xs">
        <Container>
          <Row className="text-center g-3">
            <Col xs={6} md={3}>
              <div className="fw-bold fs-4 text-slate-900" style={{ color: '#198754' }}>
                75 Years
              </div>
              <div className="text-muted small text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                Province Heritage (1951-2026)
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="fw-bold fs-4 text-slate-900" style={{ color: '#dc2626' }}>
                {stats.videos} Videos
              </div>
              <div className="text-muted small text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                Documentaries & Feasts
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="fw-bold fs-4 text-slate-900" style={{ color: '#4f46e5' }}>
                {stats.audios} Audio Records
              </div>
              <div className="text-muted small text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                Oral History & Anthems
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="fw-bold fs-4 text-slate-900" style={{ color: '#059669' }}>
                {stats.documents} Documents
              </div>
              <div className="text-muted small text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                Decrees & Souvenirs
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 3. FOUR CORE MEDIA PILLAR CARDS (Rule #3: Large Visual Cards) */}
      <section className="py-5">
        <Container>
          <div className="text-center mb-4 pb-2">
            <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-1 rounded-pill fw-bold text-uppercase mb-2">
              Separated Media Pillars
            </span>
            <h2 className="fw-bold text-slate-900" style={{ letterSpacing: '-0.5px' }}>
              Explore Dedicated Heritage Galleries
            </h2>
            <p className="text-muted small mx-auto" style={{ maxWidth: '600px' }}>
              Media types are thoughtfully separated to give each format an optimal viewing, listening, and reading experience.
            </p>
          </div>

          <Row className="g-4">
            {/* 🎥 VIDEO GALLERY CARD */}
            <Col xs={12} sm={6} lg={3}>
              <div className="media-pillar-card video" onClick={() => onNavigate('videos')}>
                <div className="media-pillar-icon-box">
                  <Film size={34} />
                </div>
                <h3 className="media-pillar-title">Video Gallery</h3>
                <p className="media-pillar-desc">
                  Watch Jubilee documentaries, historic convocation recordings, and liturgical celebrations.
                </p>
                <div className="media-pillar-btn text-danger bg-danger bg-opacity-10">
                  <span>Open Video Gallery</span>
                  <ArrowRight size={14} />
                </div>
              </div>
            </Col>

            {/* 🎧 AUDIO GALLERY CARD */}
            <Col xs={12} sm={6} lg={3}>
              <div className="media-pillar-card audio" onClick={() => onNavigate('audio')}>
                <div className="media-pillar-icon-box">
                  <Headphones size={34} />
                </div>
                <h3 className="media-pillar-title">Audio Gallery</h3>
                <p className="media-pillar-desc">
                  Listen to speeches, elder Jesuit oral histories, commemorative anthems, and homilies.
                </p>
                <div className="media-pillar-btn text-primary bg-primary bg-opacity-10">
                  <span>Open Audio Library</span>
                  <ArrowRight size={14} />
                </div>
              </div>
            </Col>

            {/* 🖼️ IMAGE GALLERY CARD */}
            <Col xs={12} sm={6} lg={3}>
              <div className="media-pillar-card image" onClick={() => onNavigate('images')}>
                <div className="media-pillar-icon-box">
                  <ImageIcon size={34} />
                </div>
                <h3 className="media-pillar-title">Image Gallery</h3>
                <p className="media-pillar-desc">
                  Explore high-resolution photographs, vintage glass plates, and portraits across 75 years.
                </p>
                <div className="media-pillar-btn text-warning bg-warning bg-opacity-10" style={{ color: '#b45309' }}>
                  <span>Open Image Gallery</span>
                  <ArrowRight size={14} />
                </div>
              </div>
            </Col>

            {/* 📄 DOCUMENT ARCHIVE CARD */}
            <Col xs={12} sm={6} lg={3}>
              <div className="media-pillar-card document" onClick={() => onNavigate('documents')}>
                <div className="media-pillar-icon-box">
                  <FileText size={34} />
                </div>
                <h3 className="media-pillar-title">Document Archive</h3>
                <p className="media-pillar-desc">
                  Read jubilee souvenir volumes, Latin decrees, and monographs in a Google Drive-style reader.
                </p>
                <div className="media-pillar-btn text-success bg-success bg-opacity-10">
                  <span>Open Document Archive</span>
                  <ArrowRight size={14} />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 4. FEATURED HISTORICAL RECORDS */}
      <section className="py-5 bg-white border-top border-bottom">
        <Container>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-4 pb-2 border-bottom">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <Sparkles size={18} className="text-warning" />
                <h3 className="fw-bold m-0 text-slate-900">Featured Jubilee Treasures</h3>
              </div>
              <p className="text-muted small m-0">
                Landmark historical records curated for the 75th Platinum Jubilee celebration.
              </p>
            </div>

            <Button
              variant="outline-success"
              size="sm"
              className="rounded-pill px-3"
              onClick={() => onNavigate('search')}
            >
              Search All Archive Records →
            </Button>
          </div>

          <Row className="g-4">
            {featuredItems.map((item) => {
              const isVid = item.mediaType === 'video';
              const isAud = item.mediaType === 'audio';
              const isDoc = item.mediaType === 'document';

              return (
                <Col key={item.id} xs={12} sm={6} lg={3}>
                  <div
                    className="jubilee-media-card cursor-pointer h-100"
                    onClick={() => handleFeaturedItemClick(item)}
                  >
                    <div className="media-thumbnail-wrapper">
                      <img src={item.thumbnailUrl} alt={item.title} className="media-thumbnail-img" />
                      <span
                        className={`media-type-badge ${
                          isVid ? 'video' : isAud ? 'audio' : isDoc ? 'document' : 'image'
                        }`}
                      >
                        {item.mediaType}
                      </span>
                      {item.duration && <span className="media-duration-badge">{item.duration}</span>}
                      {item.pageCount && <span className="media-duration-badge">{item.pageCount} pgs</span>}
                      <div className="media-play-overlay">
                        <div className="play-circle-btn">
                          {isVid ? <Film size={20} /> : isAud ? <Volume2 size={20} /> : <Eye size={20} />}
                        </div>
                      </div>
                    </div>

                    <div className="media-card-body">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: '0.72rem' }}>
                          Year {item.year}
                        </span>
                        <span className="badge bg-light text-secondary border text-truncate" style={{ fontSize: '0.72rem', maxWidth: '120px' }}>
                          {item.categoryName}
                        </span>
                      </div>

                      <h5 className="media-card-title">{item.title}</h5>
                      <p className="media-card-desc">{item.description}</p>

                      <div className="media-card-meta">
                        <span className="text-muted small text-truncate" style={{ maxWidth: '140px' }}>
                          {item.eventName || 'Jubilee Collection'}
                        </span>
                        <span className="fw-semibold text-success small">Explore →</span>
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>

      {/* 5. PROVINCE HERITAGE EMBLEMATIC SECTION */}
      <section className="py-5" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #edf2f7 100%)' }}>
        <Container style={{ maxWidth: '900px' }}>
          <div className="bg-white p-5 rounded-4 shadow-sm border text-center">
            <div className="mb-3">
              <img
                src={jubileeLogo}
                alt="Platinum Jubilee Emblem"
                className="rounded-circle shadow-md p-1 bg-white border border-2 border-warning"
                style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.src = process.env.PUBLIC_URL + '/assets/img/Jubilee-Logo.jpeg';
                }}
              />
            </div>
            <h3 className="fw-bold text-slate-900 mb-2">About Jesuit Madurai Province Archives (JEMPARC)</h3>
            <p className="text-muted small mb-4" style={{ lineHeight: '1.7' }}>
              Located in Sacred Heart College, Shembaganur, Kodaikanal, JEMPARC safeguards thousands of rare palm-leaf
              manuscripts (Olaichuvadi), mission diaries dating back to 1583, Roman decrees, and historical photographs.
              The Platinum Jubilee Digital Archive expands this heritage to researchers and faithful across the globe.
            </p>
            <div className="d-flex justify-content-center flex-wrap gap-3">
              <Button variant="success" className="rounded-pill px-4 shadow-sm fw-semibold" onClick={() => onNavigate('events')}>
                Browse 2026 Jubilee Events
              </Button>
              <Button variant="outline-dark" className="rounded-pill px-4 fw-semibold" onClick={() => onNavigate('timeline')}>
                Explore 75-Year Timeline
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Media Preview Modals */}
      <JubileeVideoPlayerModal
        video={featuredVideo}
        show={!!featuredVideo}
        onHide={() => setFeaturedVideo(null)}
        allVideos={allVideos}
      />

      <JubileeDocumentViewerModal
        document={featuredDoc}
        show={!!featuredDoc}
        onHide={() => setFeaturedDoc(null)}
      />

      <JubileeImageLightbox
        images={allImages}
        selectedIndex={featuredImageIdx}
        show={showImageLightbox}
        onHide={() => setShowImageLightbox(false)}
        onSelectIndex={(i) => setFeaturedImageIdx(i)}
      />
    </div>
  );
}
